/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* =================================================================================================
 * Custom Zanaé
 * ===============================================================================================*/

// Ouvre la pop-up newsletter au clic sur n'importe quel lien/bouton pointant vers #newsletter
// (ou portant l'attribut data-open-newsletter). Ouvre toujours, même si déjà fermée par le visiteur.
document.addEventListener('click', function (evt) {
  var trigger = evt.target.closest('a[href*="#newsletter"], [data-open-newsletter]');
  if (!trigger) return;
  // `wheel-popup` = pop-up roue (nouvelle), `pop-up` = pop-up Symmetry (ancienne).
  var popup = document.querySelector('wheel-popup, pop-up');
  if (!popup || typeof popup.open !== 'function') return;
  evt.preventDefault();
  popup.open(trigger);
});

// Code promo dans le cart drawer.
// On applique via le permalien /discount/CODE (reflété dans le cart Liquid, contrairement
// à /cart/update.js dont les codes manuels n'apparaissent que dans cart.js). Le ?redirect=/cart.js
// nous renvoie le JSON du panier pour vérifier que le code est bien applicable, puis on rafraîchit
// le drawer (re-render Liquid -> récap, barre franco et visibilité du champ se recalculent).
async function zanaeApplyDiscount(form) {
  var input = form.querySelector('input[name="discount"]');
  var errEl = form.querySelector('[data-discount-error]');
  var code = input && input.value ? input.value.trim() : '';
  if (errEl) errEl.textContent = '';
  if (!code) { if (input) input.focus(); return; }
  form.classList.add('is-loading');
  try {
    var res = await fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    var cart = await res.json();
    var applied = (cart.discount_codes || []).some(function (d) {
      return d.code && d.code.toLowerCase() === code.toLowerCase() && d.applicable;
    });
    if (!applied) {
      if (errEl) errEl.textContent = 'Ce code n’est pas valide ou ne s’applique pas à votre panier.';
      form.classList.remove('is-loading');
      return;
    }
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  } catch (e) {
    if (errEl) errEl.textContent = 'Une erreur est survenue, réessayez.';
    form.classList.remove('is-loading');
  }
}

document.addEventListener('submit', function (evt) {
  var form = evt.target.closest('[data-cart-discount-form]');
  if (!form) return;
  evt.preventDefault();
  zanaeApplyDiscount(form);
});

/* =================================================================================================
 * Tracking events — couche unique : dataLayer (pour GTM -> GA4) + Clarity en direct
 * ===============================================================================================*/
// GA4 via le gtag natif Shopify (config G-KM7ZTQ0H1E + Consent Mode v2).
// Au chargement, custom.js s'exécute AVANT que Shopify ne définisse gtag : un push dataLayer à ce
// moment précède la config GA4 et l'événement est perdu (constaté le 01/10/2026 sur les retours
// d'inscription). On attend donc gtag jusqu'à 10 s ; au-delà, fallback dataLayer (si un GTM est
// ajouté un jour).
function zanaeSendGa(name, params, tries) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    } else if (tries < 100) {
      setTimeout(function () { zanaeSendGa(name, params, tries + 1); }, 100);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    }
  } catch (e) {}
}

window.zanaeTrack = function (name, params) {
  params = params || {};
  zanaeSendGa(name, params, 0);
  // Clarity en direct (présent sur la page uniquement après consentement -> naturellement gated)
  try {
    if (window.clarity) {
      window.clarity('event', name);
      Object.keys(params).forEach(function (k) { window.clarity('set', k, String(params[k])); });
    }
  } catch (e) {}
};

// menu_opened — ouverture du menu mobile (l'ouvreur a aria-controls="main-nav" ; les fermeurs non)
document.addEventListener('click', function (evt) {
  if (evt.target.closest('.mobile-nav-toggle[aria-controls="main-nav"]')) {
    window.zanaeTrack('menu_opened');
  }
});

// newsletter_popup_closed — TOUTE fermeture du pop-up (croix, clic extérieur, Échap).
// Modal bascule l'attribut [open] ; on observe son retrait après ouverture.
(function () {
  var popup = document.querySelector('pop-up');
  if (!popup || !window.MutationObserver) return;
  var wasOpen = popup.hasAttribute('open');
  new MutationObserver(function () {
    var isOpen = popup.hasAttribute('open');
    if (wasOpen && !isOpen) window.zanaeTrack('newsletter_popup_closed');
    wasOpen = isOpen;
  }).observe(popup, { attributes: true, attributeFilter: ['open'] });
})();

// newsletter_signup — inscription newsletter validée {signup_source: wheel_popup|footer|home, method}.
// (`signup_source` et non `source`, que gtag lit comme source de trafic.)
// Le formulaire client est protégé par le captcha Shopify : l'envoi part en navigation classique
// et Shopify revient sur la page avec `customer_posted=true`. On mémorise donc la source à l'envoi
// (sessionStorage : survit à la navigation et au captcha) et on envoie l'événement au retour.
// Écoute en capture : passe avant le script captcha et la roue, qui annulent l'envoi d'origine.
// Le cas AJAX de la roue (method: ajax) est envoyé par wheel-popup.js.
document.addEventListener('submit', function (evt) {
  var form = evt.target;
  var source = form && form.getAttribute && form.getAttribute('data-signup-source');
  if (!source) return;
  try { sessionStorage.setItem('zanae_signup_source', source); } catch (e) {}
}, true);
(function () {
  if (!/[?&]customer_posted=true(&|$)/.test(window.location.search)) return;
  var source = null;
  try {
    source = sessionStorage.getItem('zanae_signup_source');
    sessionStorage.removeItem('zanae_signup_source'); // un rechargement ne recompte pas
  } catch (e) {}
  if (source) window.zanaeTrack('newsletter_signup', { signup_source: source, method: 'native' });
})();

// search_submitted — soumission du formulaire de recherche du site (param search_term)
document.addEventListener('submit', function (evt) {
  var form = evt.target.closest('form[action*="/search"]');
  if (!form) return;
  var input = form.querySelector('input[name="q"]');
  var term = input && input.value ? input.value.trim() : '';
  window.zanaeTrack('search_submitted', { search_term: term });
});

// Total (€, hors livraison) + nb d'articles, lus sur [data-cart-total]/[data-cart-items] du
// cart-form (drawer ou /cart) ; ces attributs sont tenus à jour par la fusion AJAX du panier.
function zanaeCartParams(container, surface) {
  var el = container && container.querySelector('[data-cart-items]');
  if (!el) return null;
  return {
    currency: 'EUR',
    value: parseInt(el.dataset.cartTotal, 10) / 100,
    cart_items: parseInt(el.dataset.cartItems, 10),
    cart_surface: surface
  };
}

// view_cart — chaque ouverture du drawer (clic icône, auto après ajout, réouverture), panier non vide.
// Écouteur unique sur document : le drawer n'est jamais réinjecté (seul son contenu est fusionné).
// Sur /cart, le view_cart natif Shopify part déjà : rien à ajouter (pas de doublon).
document.addEventListener('on:cart-drawer:before-open', function (evt) {
  if (evt.target.hasAttribute('open')) return; // open() rappelé sur un drawer déjà ouvert
  var params = zanaeCartParams(evt.target, 'drawer');
  if (params && params.cart_items > 0 && window.zanaeTrack) window.zanaeTrack('view_cart', params);
});

// checkout_click — boutons vers le checkout (drawer + /cart). gtag envoie en beacon : la
// redirection n'est ni retardée ni bloquée.
document.addEventListener('click', function (evt) {
  var btn = evt.target.closest('a[href*="/checkout"], [name="checkout"], .additional-checkout-buttons');
  var form = btn && btn.closest('cart-form');
  if (!form || evt.defaultPrevented) return; // defaultPrevented : CGV non cochées
  var params = zanaeCartParams(form, btn.closest('cart-drawer') ? 'drawer' : 'page');
  if (params && params.cart_items > 0 && window.zanaeTrack) window.zanaeTrack('checkout_click', params);
});

// remove_from_cart — retrait d'une ligne (drawer + /cart) : lien « Retirer », « − » à 1, ou saisie de 0.
// Le natif Shopify ne part pas depuis le drawer (vérifié en recette). En capture, pour lire la
// quantité avant que cart-form ne la passe à 0 : un 2e tap sur une ligne déjà à 0 ne compte pas.
function zanaeTrackRemove(item) {
  if (!window.zanaeTrack) return;
  window.zanaeTrack('remove_from_cart', {
    currency: 'EUR',
    value: parseInt(item.dataset.linePrice, 10) / 100,
    item_id: item.dataset.itemId,
    cart_surface: item.closest('cart-drawer') ? 'drawer' : 'page'
  });
}
document.addEventListener('click', function (evt) {
  var btn = evt.target.closest('.cart-item__remove, .quantity-down');
  var item = btn && btn.closest('cart-form .cart-item[data-item-id]');
  var input = item && item.querySelector('.cart-item__quantity-input');
  if (!input) return;
  var qty = parseInt(input.value, 10);
  if (btn.classList.contains('cart-item__remove') ? qty > 0 : qty === 1) zanaeTrackRemove(item);
}, true);
document.addEventListener('change', function (evt) {
  var input = evt.target.closest('.cart-item__quantity-input');
  var item = input && input.closest('cart-form .cart-item[data-item-id]');
  if (item && input.value === '0' && input.dataset.initialValue !== '0') zanaeTrackRemove(item);
}, true);

// gallery_interact — changement d'image sur la galerie de la fiche produit, 10 max par page vue.
(function () {
  var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!match || !document.body.classList.contains('template-product')) return;
  var handle = decodeURIComponent(match[1]);
  var count = 0;
  var lastTouch = 0;

  // Galerie de la fiche (pas celle d'un quickbuy ouvert depuis un carrousel de la page)
  function inMainGallery(el) {
    var gallery = el.closest('media-gallery');
    return !!gallery && !gallery.closest('.quickbuy-container');
  }

  function track(item, type) {
    if (!item || count >= 10 || !window.zanaeTrack) return;
    count += 1;
    var items = Array.prototype.filter.call(item.parentNode.children, function (el) { return !el.hidden; });
    window.zanaeTrack('gallery_interact', { item_id: handle, image_index: items.indexOf(item) + 1, interact_type: type });
  }

  // swipe : le carrousel principal défile aussi seul (clic miniature, changement de variante,
  // chargement) ; on ne compte que les défilements précédés d'un contact sur le carrousel.
  ['touchstart', 'pointerdown'].forEach(function (type) {
    document.addEventListener(type, function (evt) {
      var slider = evt.target.closest && evt.target.closest('carousel-slider[data-dispatch-events="true"]');
      if (slider && inMainGallery(slider)) lastTouch = Date.now();
    }, { passive: true });
  });
  document.addEventListener('on:carousel-slider:select', function (evt) {
    if (!inMainGallery(evt.target) || Date.now() - lastTouch > 2000) return;
    track(evt.detail && evt.detail.slide, 'swipe');
  });

  // thumbnail / zoom — en capture, pour lire la miniature active avant que la galerie ne change
  document.addEventListener('click', function (evt) {
    var thumb = evt.target.closest('.thumbnail');
    var zoom = evt.target.closest('.show-gallery');
    if (thumb && inMainGallery(thumb)) {
      lastTouch = 0; // le défilement du carrousel qui suit n'est pas un swipe
      if (!thumb.parentNode.classList.contains('is-active')) track(thumb.parentNode, 'thumbnail');
    } else if (zoom && inMainGallery(zoom)) {
      track(zoom.closest('.slider__item, .product-media-collage__item'), 'zoom');
    }
  }, true);
})();

/* === Cadeau offert dès 50€ : ajout/retrait automatique du bijou mystère ===
   Le seuil se calcule sur le sous-total HORS cadeau (total - ligne cadeau) pour
   éviter que le prix du bijou fausse le seuil et crée une boucle d'ajout.
   ⚠️ Garder synchro avec snippets/gift-progress-bar.liquid (variante + seuil). */
(function () {
  var GIFT_VARIANT = 57991486538060; // variante « Bijou mystère offert »
  var THRESHOLD = 5000; // 50,00 € en centimes (sous-total hors cadeau)
  var busy = false;
  var mutationTimes = []; // coupe-circuit : horodatages des modifs auto du cadeau

  function giftLine(cart) {
    for (var i = 0; i < cart.items.length; i += 1) {
      if (cart.items[i].variant_id === GIFT_VARIANT) return cart.items[i];
    }
    return null;
  }

  // Métrique STABLE (invariante à la présence du cadeau) : sous-total hors cadeau,
  // puis application du ratio de remise global (total / sous-total). Évite l'oscillation
  // quand un code promo s'applique aussi au bijou. Garder synchro avec gift-progress-bar.liquid.
  function qualifyingSubtotal(cart) {
    var line = giftLine(cart);
    var giftGross = line ? line.final_line_price : 0;
    var subtotal = cart.items_subtotal_price;
    if (!subtotal || subtotal <= 0) return 0;
    var ratio = cart.total_price / subtotal;
    return Math.round((subtotal - giftGross) * ratio);
  }

  // Coupe-circuit : si on modifie le cadeau trop souvent en peu de temps, on stoppe.
  function circuitTripped() {
    var now = Date.now();
    mutationTimes = mutationTimes.filter(function (t) { return now - t < 6000; });
    return mutationTimes.length >= 6;
  }

  function postJSON(url, data) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
    });
  }

  function syncGift() {
    if (busy) return;
    if (circuitTripped()) {
      console.warn('[cadeau] coupe-circuit déclenché : trop de modifications, arrêt automatique.');
      return;
    }
    busy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var q = qualifyingSubtotal(cart);
        var line = giftLine(cart);
        var mutation = null;
        if (q >= THRESHOLD) {
          if (!line) mutation = postJSON('/cart/add.js', { items: [{ id: GIFT_VARIANT, quantity: 1 }] });
          else if (line.quantity !== 1) mutation = postJSON('/cart/change.js', { id: line.key, quantity: 1 });
        } else if (line) {
          mutation = postJSON('/cart/change.js', { id: line.key, quantity: 0 });
        }
        if (mutation) mutationTimes.push(Date.now());
        return mutation;
      })
      .then(function (changed) {
        busy = false;
        // Un changement a eu lieu -> notifier le thème pour rafraîchir le drawer/barre.
        if (changed) document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      })
      .catch(function () { busy = false; });
  }

  // À chaque rafraîchissement du panier + au chargement de la page.
  document.addEventListener('on:cart:after-merge', syncGift);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGift);
  } else {
    syncGift();
  }
})();

/* === Upsell panier (snippets/cart-upsell.liquid) : ajout en un clic === */
document.addEventListener('click', function (evt) {
  var btn = evt.target.closest('[data-upsell-add]');
  if (!btn) return;
  btn.classList.add('is-loading');
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(btn.dataset.upsellAdd), quantity: 1 }] })
  })
    .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
    .then(function () {
      if (window.zanaeTrack) zanaeTrack('cart_upsell_add', { variant_id: btn.dataset.upsellAdd, upsell: 'magic_box' });
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    })
    .catch(function () { btn.classList.remove('is-loading'); });
});


